import {
  ComponentType,
  memo,
  PropsWithChildren,
  useEffect,
  useState,
} from 'react';

// import Sentry SDK directly since this is published package
import * as Sentry from '@sentry/browser';
import { injectIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';

import Avatar from '@atlaskit/avatar';
import Spinner from '@atlaskit/spinner';

import { urls as workspaceUrls } from 'src/urls/workspaces';
import { RecentWorkspace } from 'src/utils/recently-viewed-workspaces-store';

import urls from '../urls';

import { messages } from './account-menu.i18n';
import { DropdownItem, DropdownItemGroup } from './dropdown-helpers';

type Props = {
  /* eslint-disable react/no-unused-prop-types */
  userWorkspaceSlug: string | undefined;
  intl: IntlShape;
  recentlyViewedWorkspaces: RecentWorkspace[] | undefined;
  workspaceLinkComponent?: ComponentType<PropsWithChildren<any>>;
  hasSeparator: boolean;
  setHorizontalProfilePopupClose?: () => void;
};

type UserWorkspacesResponse = {
  values: {
    workspace: {
      uuid: string;
      slug: string;
      links: {
        avatar: { href: string };
      };
    };
  }[];
};

type WorkspaceInfo = {
  uuid: string;
  slug: string;
  overview: string;
  avatar: string;
};

const WORKSPACES_DISPLAY_COUNT = 5;

type WorkspacesKeyedById = {
  [key: string]: WorkspaceInfo;
};
export const AccountMenuWorkspaces = injectIntl(
  memo(
    ({
      intl,
      recentlyViewedWorkspaces = [],
      workspaceLinkComponent,
      hasSeparator,
      setHorizontalProfilePopupClose,
    }: Props) => {
      const workspaceHeaderLabel = intl.formatMessage(
        messages.workspacesHeader
      );

      const [workspaces, setWorkspaces] = useState<WorkspacesKeyedById>({});
      const [hasLoaded, setHasLoaded] = useState<boolean>(false);

      useEffect(() => {
        if (!recentlyViewedWorkspaces.length) {
          return;
        }

        const queries: string[] = recentlyViewedWorkspaces
          .slice(0, WORKSPACES_DISPLAY_COUNT)
          .map(w => `slug="${w.slug}"`);

        fetch(
          `/!api/2.0/user/workspaces?fields=values.workspace.uuid,values.workspace.slug,values.workspace.links.avatar.href&pagelen=${WORKSPACES_DISPLAY_COUNT}&q=${encodeURIComponent(
            queries.join(' OR ')
          )}`
        )
          .then(resp => {
            if (!resp.ok) {
              throw new Error(`${resp.status} ${resp.statusText}`);
            }
            return resp.json();
          })
          .then((body: UserWorkspacesResponse) => {
            const workspaceData = body.values.reduce(
              (obj: WorkspacesKeyedById, ws) => {
                obj[ws.workspace.uuid] = {
                  uuid: ws.workspace.uuid,
                  slug: ws.workspace.slug,
                  overview: workspaceUrls.ui.overview(ws.workspace.slug),
                  avatar: ws.workspace.links.avatar.href,
                };
                return obj;
              },
              {}
            );
            setWorkspaces(workspaceData);
          })
          .catch(e => {
            Sentry.captureException(e);
            setWorkspaces({});
          })
          .finally(() => {
            setHasLoaded(true);
          });
      }, [recentlyViewedWorkspaces]);

      return (
        <DropdownItemGroup
          title={workspaceHeaderLabel}
          key="workspaces-group"
          hasSeparator={hasSeparator}
        >
          {recentlyViewedWorkspaces?.length > 0 &&
            (hasLoaded ? (
              recentlyViewedWorkspaces.map(({ uuid }) => {
                const ws = workspaces[uuid];
                return ws ? (
                  <DropdownItem
                    href={ws.overview}
                    key={ws.uuid}
                    linkComponent={workspaceLinkComponent || undefined}
                    iconBefore={
                      <Avatar
                        size="xsmall"
                        appearance="square"
                        src={ws.avatar}
                      />
                    }
                    shouldTitleWrap={false}
                  >
                    {ws.slug}
                  </DropdownItem>
                ) : null;
              })
            ) : (
              <DropdownItem>
                <Spinner size="small" />
              </DropdownItem>
            ))}
          <DropdownItem
            href={urls.ui.allWorkspaces()}
            key={urls.ui.allWorkspaces()}
            linkComponent={workspaceLinkComponent}
            onClick={setHorizontalProfilePopupClose}
          >
            {intl.formatMessage(messages.viewAllWorkspacesLink)}
          </DropdownItem>
        </DropdownItemGroup>
      );
    }
  )
);
